import { locale } from '../lib/locale';

/**
 * Getting started (1.19): sign-in, setting up the family, joining with a code, pairing the wall
 * display, the account-mail landing page and the moving notice. German is the master.
 */
export const START_TEXT = {
  de: {
    // sign-in
    emailPrompt: 'Zur Sicherheit: Mit welcher E-Mail hast du den Link angefordert?',
    welcome: (app: string) => `Willkommen bei ${app}`,
    intro:
      'Das digitale Zuhause eurer Familie. Melde dich an oder leg in einer Minute ein Konto an.',
    google: 'Mit Google anmelden',
    or: 'oder',
    signup: 'Konto anlegen',
    forgot: 'Passwort vergessen',
    signin: 'Anmelden',
    newAccount: 'Neues Konto anlegen',
    forgotAsk: 'Passwort vergessen?',
    resetIntro: 'Wir schicken dir einen Link, mit dem du ein neues Passwort festlegst.',
    email: 'E-Mail',
    min8: 'Mindestens 8 Zeichen',
    resetSent:
      'Wenn es ein Konto mit {0} gibt, ist der Link unterwegs. Schau bitte auch im Spam-Ordner nach.',
    resetLocal: ' (Lokal: Der Link steht in der Emulator-Oberfläche unter „Authentication“.)',
    sendLink: 'Link schicken',
    newHere: 'Neu hier? Konto anlegen',
    haveAccount: 'Schon ein Konto? Anmelden',
    backToSignin: 'Zurück zur Anmeldung',
    linkUsers:
      'Bisher mit Anmelde-Link angemeldet? Tippe einmal auf „Passwort vergessen?“ – so legst du dein Passwort fest. Deine Daten bleiben.',
    testAccount: 'Testkonto (nur lokal)',
    asWall: 'Dieses Gerät koppeln',
    familyModeTitle: 'Familienmodus für ein gemeinsames Gerät',
    familyModeText:
      'Für das Tablet in der Küche, im Flur oder an der Wand: ohne Anmeldung, bleibt an, erinnert mit Gong – Kinder können nichts löschen.',
    // setup
    setupNew: {
      title: 'Unsere Familie einrichten',
      text: 'In 4 kurzen Schritten – mit fertigen Routinen und Belohnungen.',
    },
    setupInvite: {
      title: 'Ich habe einen Einladungscode',
      text: 'Ein Elternteil hat dich eingeladen.',
    },
    setupDemo: {
      title: 'Erst mal ausprobieren',
      text: 'Mit einer erfundenen Beispielfamilie. Löschbar.',
    },
    hi: 'Schön, dass du da bist!',
    howStart: 'Wie möchtest du starten?',
    signOut: 'Abmelden',
    color: 'Farbe',
    taken: 'vergeben',
    next: 'Weiter',
    stepOf: (step: string, total: string) => `Schritt ${step} von ${total}`,
    back: 'Zurück',
    settingUp: 'Wird eingerichtet …',
    defaultFamily: (name: string) => `Familie ${name}`,
    failed: 'Das hat nicht geklappt. Bitte nochmal versuchen.',
    familyAsk: 'Wie heißt eure Familie?',
    familyName: 'Familienname',
    familyExample: 'z. B. Familie Sommer',
    whoAreYou: 'Und wer bist du?',
    yourName: 'Dein Vorname oder Spitzname',
    whoElse: 'Wer gehört noch dazu?',
    namesOnly: 'Nur Vornamen oder Spitznamen – Kinder brauchen kein eigenes Konto.',
    name: 'Name',
    removePerson: (name: string) => `${name} entfernen`,
    person: 'Person',
    whoIsThis: 'Wer ist das?',
    addPerson: 'Person hinzufügen',
    pinTitle: 'Eltern-PIN festlegen',
    pinText:
      'Mit der PIN schalten Eltern am Wanddisplay den Elternmodus frei – zum Beispiel zum Freigeben. Kinder kennen sie besser nicht.',
    pin: 'PIN (4–6 Ziffern)',
    pinAgain: 'PIN wiederholen',
    pinsDiffer: 'Die beiden PINs sind verschieden.',
    start: 'Zum Start',
    withTemplates: 'Mit Vorlagen',
    withTemplatesHint: 'Routinen je Alter, Belohnungen, Familienziel',
    empty: 'Leer starten',
    emptyHint: 'alles selbst anlegen',
    finish: 'Fertig – los geht’s',
    demoTitle: 'Ausprobieren mit Beispielfamilie',
    demoLoad: 'Beispielfamilie laden',
    demoFamily: 'Familie Beispiel',
    demoText:
      'Eine Familie mit Mama, Papa, Jonas (14), Lotta (9) und Ben (5) – mit Terminen, Stundenplänen, Aufgaben und Sternen. Die Eltern-PIN ist {0}.',
    whoAreYouShort: 'Wer bist du?',
    mum: 'Mama',
    dad: 'Papa',
    firstName: 'Dein Vorname',
    demoLater:
      'Später löschst du die Beispieldaten unter Einstellungen → Familie und legst eure eigene Familie an.',
    inviteTitle: 'Einladungscode eingeben',
    join: 'Beitreten',
    badCode: 'Der Code ist ungültig oder abgelaufen. Bitte einen neuen anfordern.',
    inviteHow:
      'Den Code bekommst du von einem Elternteil: Einstellungen → Familie → Person → „Einladen“.',
    code: 'Code',
    // pairing
    wallName: 'Familiengerät',
    pairFailed: 'Der Code stimmt nicht oder ist abgelaufen. Erzeuge auf dem Handy einen neuen.',
    pairing: 'Gerät wird verbunden …',
    toSignin: 'Zur Anmeldung',
    setupWall: 'Familienmodus einrichten',
    setupWallIntro:
      'Für das Gerät, das alle nutzen – in der Küche, im Flur oder an der Wand. Es bleibt an, wird nachts ruhig, erinnert mit Gong, und Kinder können nichts löschen.',
    howTo: 'So geht’s:',
    pairSteps: [
      'Auf dem Eltern-Handy: Einstellungen → Familienmodus & PIN → „Gerät koppeln“',
      'Den QR-Code mit der Kamera dieses Geräts scannen – oder den 6-stelligen Code hier eintippen',
      'Fertig – das Gerät zeigt eure Familie',
    ],
    pairCode: 'Code vom Eltern-Handy',
    // account mails
    moment: 'Einen Moment …',
    newPassword: 'Neues Passwort festlegen',
    forEmail: 'Für {0}. Danach bist du gleich angemeldet.',
    newPasswordLabel: 'Neues Passwort',
    savePassword: 'Passwort speichern',
    verified: 'E-Mail bestätigt',
    verifiedText: 'Danke! Deine E-Mail-Adresse ist jetzt bestätigt.',
    toApp: 'Zur App',
    notWorked: 'Das hat nicht geklappt',
    wrongPassword: 'Das Passwort stimmt nicht.',
    yourFamily: 'Eure Familie',
    // moving notice
    moved: 'Wir sind umgezogen',
    movedText:
      'SweetHomeHub heißt jetzt {0} und wohnt unter {1}. Eure Daten sind schon da – nichts geht verloren.',
    toNewAddress: 'Zur neuen Adresse',
    onceNeeded: 'Einmal kurz nötig',
    movedSteps: [
      'Einmal neu anmelden – mit Google oder deiner E-Mail.',
      'Die alte App vom Homebildschirm löschen und die neue Adresse dort hinzufügen.',
      'Das Wanddisplay neu koppeln: Einstellungen → Wanddisplay & PIN.',
      'Erinnerungen neu erlauben: Einstellungen → Dieses Gerät.',
    ],
  },
  en: {
    emailPrompt: 'For your security: which email did you request the link with?',
    welcome: (app: string) => `Welcome to ${app}`,
    intro: 'Your family’s digital home. Sign in or create an account in a minute.',
    google: 'Sign in with Google',
    or: 'or',
    signup: 'Create account',
    forgot: 'Forgot password',
    signin: 'Sign in',
    newAccount: 'Create a new account',
    forgotAsk: 'Forgot password?',
    resetIntro: 'We’ll send you a link to set a new password.',
    email: 'Email',
    min8: 'At least 8 characters',
    resetSent:
      'If there’s an account with {0}, the link is on its way. Please check your spam folder too.',
    resetLocal: ' (Local: the link is in the emulator UI under “Authentication”.)',
    sendLink: 'Send link',
    newHere: 'New here? Create an account',
    haveAccount: 'Already have an account? Sign in',
    backToSignin: 'Back to sign-in',
    linkUsers:
      'Used to sign in with a link? Tap “Forgot password?” once – that’s how you set your password. Your data stays.',
    testAccount: 'Test account (local only)',
    asWall: 'Pair this device',
    familyModeTitle: 'Family mode for a shared device',
    familyModeText:
      'For the tablet in the kitchen, the hall or on the wall: no sign-in, stays on, reminds you with a chime – kids can’t delete anything.',
    setupNew: {
      title: 'Set up our family',
      text: 'In 4 short steps – with ready-made routines and rewards.',
    },
    setupInvite: { title: 'I have an invitation code', text: 'A parent has invited you.' },
    setupDemo: {
      title: 'Just try it out first',
      text: 'With a made-up example family. Can be deleted.',
    },
    hi: 'Lovely to have you here!',
    howStart: 'How would you like to start?',
    signOut: 'Sign out',
    color: 'Colour',
    taken: 'taken',
    next: 'Next',
    stepOf: (step: string, total: string) => `Step ${step} of ${total}`,
    back: 'Back',
    settingUp: 'Setting up …',
    defaultFamily: (name: string) => `${name}’s family`,
    failed: 'That didn’t work. Please try again.',
    familyAsk: 'What’s your family called?',
    familyName: 'Family name',
    familyExample: 'e.g. The Summers',
    whoAreYou: 'And who are you?',
    yourName: 'Your first name or nickname',
    whoElse: 'Who else belongs to it?',
    namesOnly: 'First names or nicknames only – kids don’t need their own account.',
    name: 'Name',
    removePerson: (name: string) => `Remove ${name}`,
    person: 'person',
    whoIsThis: 'Who is this?',
    addPerson: 'Add person',
    pinTitle: 'Set the parent PIN',
    pinText:
      'With the PIN, parents unlock parent mode on the wall display – for approvals, for example. Best if the kids don’t know it.',
    pin: 'PIN (4–6 digits)',
    pinAgain: 'Repeat PIN',
    pinsDiffer: 'The two PINs don’t match.',
    start: 'To start with',
    withTemplates: 'With templates',
    withTemplatesHint: 'Routines by age, rewards, family goal',
    empty: 'Start empty',
    emptyHint: 'add everything yourself',
    finish: 'Done – let’s go',
    demoTitle: 'Try it with an example family',
    demoLoad: 'Load the example family',
    demoFamily: 'Example family',
    demoText:
      'A family with Mum, Dad, Jonas (14), Lotta (9) and Ben (5) – with events, timetables, tasks and stars. The parent PIN is {0}.',
    whoAreYouShort: 'Who are you?',
    mum: 'Mum',
    dad: 'Dad',
    firstName: 'Your first name',
    demoLater:
      'Later you delete the example data under Settings → Family and set up your own family.',
    inviteTitle: 'Enter invitation code',
    join: 'Join',
    badCode: 'The code is invalid or has expired. Please ask for a new one.',
    inviteHow: 'A parent gives you the code: Settings → Family → person → “Invite”.',
    code: 'Code',
    wallName: 'Family device',
    pairFailed: 'The code is wrong or has expired. Create a new one on the phone.',
    pairing: 'Connecting the device …',
    toSignin: 'Go to sign-in',
    setupWall: 'Set up family mode',
    setupWallIntro:
      'For the device everyone uses – in the kitchen, the hall or on the wall. It stays on, goes quiet at night, reminds you with a chime, and kids can’t delete anything.',
    howTo: 'How it works:',
    pairSteps: [
      'On a parent’s phone: Settings → Family mode & PIN → “Pair device”',
      'Scan the QR code with this device’s camera – or type the 6-digit code here',
      'Done – the device shows your family',
    ],
    pairCode: 'Code from the parent’s phone',
    moment: 'One moment …',
    newPassword: 'Set a new password',
    forEmail: 'For {0}. You’ll be signed in straight away.',
    newPasswordLabel: 'New password',
    savePassword: 'Save password',
    verified: 'Email confirmed',
    verifiedText: 'Thank you! Your email address is now confirmed.',
    toApp: 'Go to the app',
    notWorked: 'That didn’t work',
    wrongPassword: 'The password is wrong.',
    yourFamily: 'Your family',
    moved: 'We’ve moved',
    movedText:
      'SweetHomeHub is now called {0} and lives at {1}. Your data is already there – nothing is lost.',
    toNewAddress: 'Go to the new address',
    onceNeeded: 'Needed once',
    movedSteps: [
      'Sign in again once – with Google or your email.',
      'Delete the old app from your home screen and add the new address there.',
      'Pair the wall display again: Settings → Wall display & PIN.',
      'Allow reminders again: Settings → This device.',
    ],
  },
} as const;

export const startText = START_TEXT[locale];
